<template>
  <div
    class="talents-world-page absolute fill-height"
    :class="{dragging: mousedown}"
    @mouseleave="onMouseLeave"

    @mousedown="onMouseDown"
    @touchstart="onMouseDown"

    @mouseup="onMouseUp"
    @touchend="onMouseUp"

    @mousemove.prevent="onMouseMove"
    @touchmove.prevent="onMouseMove"
    >
    <div class="absolute draggable" :style="draggableStyle">
      <transition>
        <div class="grid centered" v-if="SocialElements">
          <router-link
            exact
            @mouseenter.native="rolloverItem = item"
            @mouseleave.native="rolloverItem = null"
            data-cursor-target="center center"
            v-for="(item, i) in SocialElements.items"
            :key="item._slug" :to="{params: {itemSlug: item._slug}}"
            :aria-label="item.title"
            :style="linkStyle(i, item)"
            :class="{'elevation-4': rolloverItem !== item && popupForItem !== item, 'elevation-12': rolloverItem === item || popupForItem === item}"
            >
            <seo-v-img
              :src="`/media/${item.coverImage.id}`"
              :lazy-src="JSON.parse(item.coverImage.metaData)['image-lazy-src']"
              :width="linkSize(item).width"
              :height="linkSize(item).height"
              />
          </router-link>
        </div>
      </transition>

      <transition>
        <div class="centered text" v-if="SocialElements && !rolloverItem">
          <h1><CharSplitText style="--appear-delay: 100ms;" :text="$root.siteSettings.socialsPageTitle" /></h1>
          <transition appear name="from-below"><MarkdownBlock style="--appear-delay: 200ms;" :text="$vuetify.breakpoint.mdAndUp ? $root.siteSettings.socialsPageDescription : reducedDescription" /></transition>
        </div>
      </transition>

    </div>
    <transition>
      <div class="centered rollover-title" v-if="rolloverItem" :key="rolloverItem.id"><CharSplitText style="--appear-delay: 100ms;" :text="rolloverItem.title" /></div>
    </transition>
    <ClickDragTutorial vtw />
    <v-dialog fullscreen persistent transition="fade-transition" :value="popupForItem" @input="onGalleryInputChange" overlay-color="#f4f4f8" :overlay-opacity="1">
      <ApolloQuery :skip="!itemSlug" :query="getSocialElement" :variables="{status: 'PUBLISHED', slug: itemSlug}" :tag="null" v-slot="{ result: { loading, data } }" notify-on-network-status-change>
        <v-sheet class="fill-height">
          <v-row align="center" justify="center" class="fill-height">
            <template v-if="data && data.SocialElement && !loading">
              <v-col align-self="start" class="text-center pt-16 pb-0" cols="12"><v-btn aria-label="Close" data-cursor-target="center center" icon exact :to="{ params: { itemSlug: null } }"><CloseIcon class="close-icon" /></v-btn></v-col>
              <ClickDragTutorial v-if="!data.SocialElement.embeddedVideoURL" class="gallery-tutorial" />
              <v-col align-self="center" justify-self="center" cols="12">
                <vue-plyr v-if="!$root.platform.prerenderer && data.SocialElement.embeddedVideoURL && itemSlug" class="plyr" :emit="['playing','pause', 'ended']" @playing="$root.soundManager.mute('content')" @pause="$root.soundManager.unmute('content')" @ended="$root.soundManager.unmute('content')">
                  <div :data-plyr-provider="data.SocialElement.embeddedVideoURL.indexOf('vimeo.') !== -1 ? 'vimeo': 'youtube'" :data-plyr-embed-id="getVideoId(data.SocialElement.embeddedVideoURL).id"></div>
                </vue-plyr>
                  <!-- :height="$vuetify.breakpoint.smAndDown ? viewPort.height * 0.5 : viewPort.height * 0.5" -->
                <transition appear name="from-below">
                  <ContentGallery2
                    v-if="!data.SocialElement.embeddedVideoURL && data.SocialElement.content && data.SocialElement.content.length > 1"
                    :controls="false"
                    :data="data.SocialElement.content"
                    display-mode="full"
                    wheel
                    no-space-right
                    />
                </transition>
              </v-col>
              <v-col cols="12" align-self="end" class="text-center gallery-title pb-16 mb-16" :key="itemSlug">
                <CharSplitText :text="data.SocialElement.title" />
              </v-col>
            </template>
            <template v-else>
              <LoadingSpinner />
            </template>
          </v-row>
        </v-sheet>
      </ApolloQuery>
    </v-dialog>
    <transition><LoadingSpinner v-if="!SocialElements" class="centered" /></transition>
  </div>
</template>

<script>
import ContentGallery2 from '@/atoms/ContentGallery2'
import ListSocialElement from '@/graphql/ListSocialElement.gql'
import GetSocialElement from '@/graphql/GetSocialElement.gql'
import { ElasticNumber, RAF } from '@monogrid/js-utils'
import { Viewport, MarkdownBlock } from '@monogrid/vue-lib'
import find from 'lodash/find'
import CloseIcon from '@/assets/img/close.svg'
import getVideoId from 'get-video-id'
import ClickDragTutorial from '@/atoms/ClickDragTutorial'
import CharSplitText from '@/atoms/CharSplitText'
import LoadingSpinner from '@/atoms/LoadingSpinner'

export default {
  name: 'SocialsPage',
  mixins: [Viewport],
  components: {
    ContentGallery2,
    MarkdownBlock,
    CloseIcon,
    ClickDragTutorial,
    CharSplitText,
    LoadingSpinner
  },
  props: {
    itemSlug: {
      type: String,
      required: false
    }
  },
  apollo: {
    SocialElements: {
      query: ListSocialElement,
      variables: { status: 'PUBLISHED', descending: true },
      result ({ data: { SocialElements } }) {
        this.zPositions = SocialElements.items.map(a => Math.random())
      }
    }
  },

  data () {
    return {
      getSocialElement: GetSocialElement,
      mousedown: false,
      startX: 0,
      startY: 0,
      walkX: 0,
      walkY: 0,
      startElasticX: 0,
      startElasticY: 0,
      elasticX: new ElasticNumber(0),
      elasticY: new ElasticNumber(0),
      rolloverItem: null
    }
  },

  mounted () {
    this.time = new Date().getTime()
    RAF.add(this.onUpdate)
  },

  computed: {
    reducedDescription () {
      // const desc = this.$root.siteSettings.socialsPageDescription.split(' ')
      // return desc.slice(0, 20).join(' ') + ' ...'
      return this.$root.siteSettings.socialsPageDescription
    },
    popupForItem () {
      if (this.itemSlug && this.SocialElements) {
        return find(this.SocialElements.items, { _slug: this.itemSlug })
      }
      return null
    },
    draggableStyle () {
      return {
        transform: `translate3d(${this.elasticX.value}px, ${this.elasticY.value}px, 0)`
      }
    }
  },

  methods: {
    getVideoId,
    onGalleryInputChange (value) {
      if (!value && this.itemSlug) {
        this.$router.push({ params: { itemSlug: null } })
      }
    },
    onUpdate () {
      const now = new Date().getTime()
      const delta = (now - this.time) / 1000
      if (!this.itemSlug) {
        const maxCoords = [-999999, -999999]
        const minCoords = [999999, 999999]
        if (this.SocialElements && this.SocialElements.items) {
          for (let i = 0; i < this.SocialElements.items.length; i++) {
            const coord = this.outward(i)
            maxCoords[0] = Math.max(coord[0], maxCoords[0])
            maxCoords[1] = Math.max(coord[1], maxCoords[1])

            minCoords[0] = Math.min(coord[0], minCoords[0])
            minCoords[1] = Math.min(coord[1], minCoords[1])
          }
          const spacing = this.viewPort.height * 0.7
          // console.log(maxCoords, minCoords)
          const maxX = maxCoords[0] * spacing
          const maxY = maxCoords[1] * spacing

          const minX = minCoords[0] * spacing
          const minY = minCoords[1] * spacing

          this.elasticX.target = Math.min(Math.max(this.elasticX.target, -maxX), -minX)
          this.elasticY.target = Math.min(Math.max(this.elasticY.target, -maxY), -minY)
        }
        this.elasticX.update(Math.min(delta, 1 / 10))
        this.elasticY.update(Math.min(delta, 1 / 10))
      }
      this.time = now
    },
    linkSize (item) {
      const aspect = this.getAspect(item.coverImage)
      if (aspect < 1) {
        return {
          width: this.viewPort.height * 0.2,
          height: (this.viewPort.height * 0.2) / aspect
        }
      } else {
        return {
          height: this.viewPort.height * 0.2,
          width: (this.viewPort.height * 0.2) * aspect
        }
      }
    },
    linkStyle (n, item) {
      const pos = this.outward(n)
      // console.log(n, pos)
      const centerX = 0
      const centerY = -this.viewPort.height * 0.1
      const firstRowMultiplier = this.viewPort.height * 0.8
      const secondRowMultiplier = this.viewPort.height * 0.5
      const spacing = n <= 5 ? firstRowMultiplier : secondRowMultiplier

      const x = centerX + spacing * (pos[0] * (n > 5 ? 1.3 : 1))
      const y = centerY + spacing * (pos[1] * (n > 5 ? 1.3 : 1))

      const scale = this.rolloverItem === item || this.popupForItem === item ? 1.4 : 1.3
      return {
        opacity: (this.rolloverItem && this.rolloverItem !== item) || (this.popupForItem && this.popupForItem !== item) ? 0.2 : 1,
        transform: `translate3d(${x}px, ${y}px, ${this.zPositions[n] * -300}px) scale(${scale})`
      }
    },

    getAspect (image) {
      const meta = JSON.parse(image.metaData)
      return parseFloat(meta['image-width']) / parseFloat(meta['image-height'])
    },
    /**
     * https://jsfiddle.net/Drakes/Lsadh0h3/
     */
    // spiral (n) {
    //   // given n an index in the squared spiral
    //   // p the sum of point in inner square
    //   // a the position on the current square
    //   // n = p + a
    //   const r = Math.floor((Math.sqrt(n + 1) - 1) / 2) + 1
    //   // compute radius : inverse arithmetic sum of 8+16+24+...=
    //   const p = (8 * r * (r - 1)) / 2
    //   // compute total point on radius -1 : arithmetic sum of 8+16+24+...
    //   const en = r * 2
    //   // points by face
    //   const a = (1 + n - p) % (r * 8)
    //   // compute de position and shift it so the first is (-r,-r) but (-r+1,-r)
    //   // so square can connect
    //   const pos = [0, 0, r]
    //   switch (Math.floor(a / (r * 2))) {
    //     // find the face : 0 top, 1 right, 2, bottom, 3 left
    //     case 0:
    //       pos[0] = a - r
    //       pos[1] = -r
    //       break
    //     case 1:
    //       pos[0] = r
    //       pos[1] = (a % en) - r
    //       break
    //     case 2:
    //       pos[0] = r - (a % en)
    //       pos[1] = r
    //       break
    //     case 3:
    //       pos[0] = -r
    //       pos[1] = r - (a % en)
    //       break
    //   }
    //   return pos
    // },
    outward (n) {
      // const s = 5
      // 8 : 2 = 5 : x

      var r = Math.floor((Math.sqrt(n + 1) - 1) / 1.44) + 1
      const steps = 5 * r
      const phase = (r * 0.1) + 2 * Math.PI * n / steps
      const x = r * Math.cos(phase)
      const y = r * Math.sin(phase)

      return [x, y, r]
    },
    onMouseDown (e) {
      this.mousedown = true
      this.startX = e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX
      this.startY = e.touches && e.touches.length > 0 ? e.touches[0].pageY : e.pageY
      this.startElasticX = this.elasticX.target
      this.startElasticY = this.elasticY.target
    },
    onMouseMove (e) {
      if (!this.mousedown) return
      this.walkX = (e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX) - this.startX
      this.walkY = (e.touches && e.touches.length > 0 ? e.touches[0].pageY : e.pageY) - this.startY

      this.elasticX.target = this.startElasticX + this.walkX
      this.elasticY.target = this.startElasticY + this.walkY
    },
    onMouseLeave (e) {
      this.mousedown = false
      this.startX = e.touches && e.touches.length > 0 ? e.touches[0].pageX : e.pageX
      this.startY = e.touches && e.touches.length > 0 ? e.touches[0].pageY : e.pageY
      this.onMouseUp(e)
    },

    onMouseUp (e) {
      this.mousedown = false
      this.walkX = 0
      this.walkY = 0
    }
  },

  metaInfo () {
    const pageName = 'socials'
    const imageMeta = JSON.parse(this.$root.siteSettings[`${pageName}PageShareImage`].metaData)
    return {
      // home page browser title
      title: this.$root.siteSettings[`${pageName}PageTitle`],
      meta: [
        { vmid: 'description', name: 'description', content: this.$root.siteSettings[`${pageName}PageShareDescription`] },
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: `${this.$root.siteSettings[`${pageName}PageTitle`]} | ${this.$root.siteSettings.siteTitle}` },
        { vmid: 'ogdescription', property: 'og:description', content: this.$root.siteSettings[`${pageName}PageShareDescription`] },
        { vmid: 'ogimage', property: 'og:image', content: `https://${this.$root.process.env.SERVICE_TARGET_DOMAIN}/media/${this.$root.siteSettings[`${pageName}PageShareImage`].id}` },
        { vmid: 'ogimagetype', property: 'og:image:type', content: this.$root.siteSettings[`${pageName}PageShareImage`].mimeType },
        { vmid: 'ogimageheight', property: 'og:image:width', content: imageMeta['image-width'] },
        { vmid: 'ogimagewidth', property: 'og:image:height', content: imageMeta['image-height'] }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>

.talents-world-page {
  overflow: hidden;
  perspective: 1000px;

  cursor: grab;

  &.dragging {
    cursor: grabbing;
  }
}

h1 {
  color: $c-pink;
  font-size: 7vh;
  // max-width: 40vw;
  text-align: center;
  margin: 0 auto;
  margin-bottom: 20px;
}

.draggable {
  transform-style: preserve-3d;
}

.absolute {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.centered {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate3d(-50%, -50%, 0);
}

.text {
  transform: translate3d(-50%, -50%, 100px);
  text-align: center;
  font-size: 0.8rem;
  max-width: 40vw;
  user-select: none;
  pointer-events: none;

  @include breakpoint('sm-and-down') {
    max-width: 90vw;
    width: 90vw;
  }

  p {
    text-shadow: 1px 1px 1px rgba(255, 255, 255, 1);
  }

  a {
    pointer-events: all;
    text-decoration: underline;
  }
}

.grid {
  // perspective: 1000px;
  user-select: none;
  transform-style: preserve-3d;
  transition: transform 0.3s $ease-in-out-cubic;

  .dragging & {
    transform: translate3d(0, 0, -20px);
  }

  a {
    position: absolute;
    display: block;
    left: 0;
    top: 0;
    transition: all 0.3s $ease-in-out-cubic;
    padding: 20px;
    background-color: map-get($shades, 'white');
    // left: 50%;
    // top: 50%;

    .v-image {
      position: relative;
      display: block;
      // left: 0;
      // top: 0;
      // left: 50%;
      // top: 50%;
      // transform: translate(-50%, -50%);
    }
  }
}

.rollover-title {
  font-family: $heading-font-family;
  font-size: 6vh;
  pointer-events: none;
  text-align: center;
  max-width: 60vw;
}

.gallery-title {
  font-family: $heading-font-family;
  font-size: 4vh;
  pointer-events: none;
  text-align: center;
  opacity: 0.3;
}

.help {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translate3d(-50%, 0, 0);
  pointer-events: none;
}

.close-icon {
  width: 12px;
  height: 12px;
  color: map-get($shades, 'black');
  stroke: currentColor;
}

::v-deep {
  .plyr {
    height: 100%;
    max-height: 78vh;
  }

  .v-dialog {
    box-shadow: none !important;
    overflow: hidden;
    // height: 50vh;
  }
}

.tutorial {
  position: absolute;
  bottom: 0;
  width: 100%;
}

.gallery-tutorial {
  bottom: initial;
  position: relative;

  ::v-deep {
    h5 {
      margin-bottom: 0;
    }
  }
}

</style>
